/**
 * RBAC-style gate for viewing a medical PA order (admin bypass, facility + assignee match).
 *
 * @module useOrderAccessPermission
 */
import {
  getTokenUserInfo,
  isCurrentUserAdmin,
  isCurrentUserSuperAdmin,
} from "@risalabs_frontend_org/auth-wrapper";
import { useMemo } from "react";
import { GeneralOrderDetails } from "../data-model/medicalPaOrdersModel";

/**
 * Permission check result for order access
 */
export interface OrderAccessPermissionResult {
  /** Whether the user has permission to view the order */
  hasAccess: boolean;
  /** Error message to display when access is denied */
  errorMessage: string | null;
}

/**
 * Custom hook to check if the current user has permission to view an order.
 *
 * Access is granted if:
 * 1. User is an Admin (admin_med_onc role) - always allowed
 * 2. User is a Super Admin - always allowed
 * 3. For regular members, BOTH conditions must be met:
 *    - The order's assigned facility ID is in the user's facilities (internal or external)
 *    - AND the order's assigned provider ID matches the current user's ID
 *
 * @param order - The medical PA order to check access for
 * @param isLoading - Whether the order is still loading
 * @returns Object containing hasAccess boolean and errorMessage if denied
 */
export function useOrderAccessPermission(
  order: GeneralOrderDetails | null | undefined,
  isLoading: boolean = false,
): OrderAccessPermissionResult {
  return useMemo(() => {
    if (isLoading || !order) {
      return { hasAccess: true, errorMessage: null };
    }
    if (isCurrentUserAdmin() || isCurrentUserSuperAdmin()) {
      return { hasAccess: true, errorMessage: null };
    }

    const userInfo = getTokenUserInfo();

    if (!userInfo) {
      return {
        hasAccess: false,
        errorMessage:
          "Unable to verify your permissions. Please try logging in again.",
      };
    }

    const currentUserId = userInfo.id;

    const userFacilities = [
      ...(userInfo.internal_healthcare_facilities ?? []),
      ...(userInfo.external_healthcare_facilities ?? []),
      ...(userInfo.healthcare_facilities ?? []),
    ];

    const orderOrgId = order?.org_id;
    const orderAssignedProviderId = order?.assigned_to?.assigned_to_id;

    // Check if user has access - BOTH conditions must be met:
    // 1. Order's facility is in user's facilities
    // 2. Order is assigned to the current user
    const isOrderInUserFacility =
      orderOrgId && userFacilities.includes(orderOrgId);
    const isOrderAssignedToCurrentUser =
      orderAssignedProviderId && orderAssignedProviderId === currentUserId;

    if (isOrderInUserFacility && isOrderAssignedToCurrentUser) {
      return { hasAccess: true, errorMessage: null };
    }

    return {
      hasAccess: false,
      errorMessage:
        "You don't have the permission to view this order. Please contact your administrator.",
    };
  }, [order, isLoading]);
}
